/**
 * SerbestFormlarPage.jsx — serbest (esnek) form yönetiminin tek ekranı
 * Route: /Management/FreeForm?tab=doldur|sablonlar|gonderilen
 *
 * 2026-09-12'ye kadar "Esnek Formlar" adında ayrı bir üst menüydü, üç girdiye
 * dağılmıştı:
 *
 *   Form Doldur                → /FdsSaha/FreeForm             (FreeFormSahaListPage)
 *   Esnek Form Şablonları      → /Management/FreeForm/templates   (FreeFormTemplatePage)
 *   Gönderilen Esnek Formlar   → /Management/FreeForm/submissions (FreeFormSubmissionsPage)
 *
 * Artık Form Yönetimi > Diğer Formlar altında tek sayfa, üç sekme. Üç ekran da
 * `embedded` bayrağıyla kendi sayfa kabuğunu kapatır (bkz. Components/Common/
 * EmbeddablePage.jsx); kendi rotaları derin bağlantılar için duruyor.
 *
 * Yetki: şablon ve gönderim ekranlarının kendi rotaları rol [1,2,3] ile korunuyor.
 * Bu sayfa daha geniş bir rol kümesine açık olduğu için o iki sekme burada da
 * sadece yönetim gruplarına gösterilir — denetçi için yalnız "Form Doldur" kalır.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container, Card, Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FreeFormSahaListPage from "../../FdsSaha/FreeFormSahaListPage";
import FreeFormTemplatePage from "../FreeForm/FreeFormTemplatePage";
import FreeFormSubmissionsPage from "../FreeForm/FreeFormSubmissionsPage";
// WAS (until #325): import { getCurrentPageAccessGroupId } from "../../../Routes/pageAccess";
// #325: the templates / submissions tabs open with the form-design page permission, not role ids.
import { useYetki, SAYFA_IZNI } from "../../../services/rbacService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// WAS (until #325): /** Yönetim grupları — şablon/gönderim sekmelerini yalnız bunlar görür. */
// const ADMIN_GROUP_IDS = [1, 2, 3];

// #302: tab access follows the role whose PAGES this login gets (a new role: its template's).
// WAS:
// function getCurrentGroupId() {
//   try {
//     const groupInfo = JSON.parse(localStorage.getItem("groupInfo"));
//     return groupInfo?.id ?? groupInfo?.userGroupEntity?.id ?? null;
//   } catch {
//     return null;
//   }
// }
// WAS (until #325):
// function getCurrentGroupId() {
//   return getCurrentPageAccessGroupId();
// }

const ALL_TABS = [
  { key: "doldur",     get label() { return i18n.t("formlar:serbestFormlar.formDoldur"); },       icon: "bx-edit",     adminOnly: false },
  { key: "sablonlar",  get label() { return i18n.t("formlar:serbestFormlar.sablonlar"); },         icon: "bx-layer",    adminOnly: true  },
  { key: "gonderilen", get label() { return i18n.t("formlar:serbestFormlar.gonderilenFormlar"); }, icon: "bx-archive", adminOnly: true  },
];

export default function SerbestFormlarPage() {
  const { t: tx } = useTranslation("formlar");
  const [searchParams, setSearchParams] = useSearchParams();

  // WAS (until #325): const isAdmin = ADMIN_GROUP_IDS.includes(getCurrentGroupId());
  const isAdmin = useYetki(SAYFA_IZNI.FORM_YONETIM) === true;
  const tabs    = ALL_TABS.filter((t) => isAdmin || !t.adminOnly);

  const tabParam  = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.key === tabParam) ? tabParam : tabs[0].key;

  // Açılmış sekme mount edilmiş kalsın (yarım kalan form kaybolmasın);
  // hiç açılmamış sekme render edilmez ki üç ekran birden veri çekmesin.
  const [visited, setVisited] = useState(() => ({ [activeTab]: true }));
  const isMounted = (key) => key === activeTab || visited[key];

  useEffect(() => { document.title = i18n.t("formlar:serbestFormlar.serbestFormlarFds"); }, []);

  const setTab = (key) => {
    setVisited((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("serbestFormlar.serbestFormlar")} pageTitle={tx("serbestFormlar.formYonetimi")} />

        <Card className="mb-0">
          <Nav tabs className="nav-tabs-custom border-bottom-0 px-3 pt-2">
            {tabs.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  className={`fw-semibold ${activeTab === t.key ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                >
                  <i className={`bx ${t.icon} me-1 align-middle`} />
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Card>

        <TabContent activeTab={activeTab} className="mt-3">
          <TabPane tabId="doldur">
            {isMounted("doldur") && <FreeFormSahaListPage embedded />}
          </TabPane>
          {isAdmin && (
            <TabPane tabId="sablonlar">
              {isMounted("sablonlar") && <FreeFormTemplatePage embedded />}
            </TabPane>
          )}
          {isAdmin && (
            <TabPane tabId="gonderilen">
              {isMounted("gonderilen") && <FreeFormSubmissionsPage embedded />}
            </TabPane>
          )}
        </TabContent>
      </Container>
    </div>
  );
}
